import { Button } from "@appretio/ui-kit";
import { useMemo, useState } from "react";
import { getFieldLabel } from "../../forecastContext";
import { useContextFilters } from "../../hooks/manageCubes/useContextFilters";
import { useCubeContext } from "../../hooks/manageCubes/useCubeContext";
import { useManageCubesOperationToast } from "../../hooks/manageCubes/useManageCubesOperationToast";
import { useTrimCube } from "../../hooks/manageCubes/useTrimCube";
import { ConfirmOperationDialog } from "./ConfirmOperationDialog";
import { CubeContextFilterGrid } from "./CubeContextFilterGrid";
import { buttonRow, formColumn, hintText } from "./manageCubesOperations.css";

/** Props for the Trim Cube operation form, which deletes cube data matching a context filter. */
interface TrimOperationProps {
  /** The cube ID to trim data from. */
  cubeId: string;
}

export function TrimOperation({ cubeId }: TrimOperationProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { showSuccess, ToastElement } = useManageCubesOperationToast();
  const { selections, activeFilters, handleFieldChange, resetSelections } =
    useContextFilters();

  const { data: cubeContext, isLoading: contextLoading } =
    useCubeContext(cubeId);

  const { data: filteredContext } = useCubeContext(cubeId, activeFilters);
  const trimCube = useTrimCube();

  const hasSelection = useMemo(
    () => Object.values(selections).some((v) => v.length > 0),
    [selections]
  );

  const activeSelections = useMemo(
    () =>
      Object.entries(selections).filter(([, values]) => values.length > 0) as [
        string,
        string[]
      ][],
    [selections]
  );

  function handleConfirm() {
    const context: Record<string, string[]> = {};
    for (const [key, values] of activeSelections) {
      context[key] = values;
    }

    trimCube.mutate(
      { cube_id: cubeId, context },
      {
        onSuccess: (data) => {
          showSuccess(data.message);
          resetSelections();
          setDialogOpen(false);
        },
        // The global mutation handler already toasts the failure with the
        // server's reason — a local toast would duplicate it.
        onError: () => {
          setDialogOpen(false);
        }
      }
    );
  }

  return (
    <>
      {ToastElement}
      <div className={formColumn}>
        <p className={hintText}>Filter which part of the cube to trim.</p>
        <CubeContextFilterGrid
          options={cubeContext?.options}
          filteredOptions={filteredContext?.options}
          selections={selections}
          hasActiveFilters={!!activeFilters}
          isLoading={contextLoading}
          setSelected={handleFieldChange}
        />
        <div className={buttonRow}>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setDialogOpen(true)}
            disabled={!hasSelection}
          >
            Trim
          </Button>
        </div>
      </div>
      <ConfirmOperationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title="Trim Cube"
        confirmText="Trim"
        onConfirm={handleConfirm}
        isLoading={trimCube.isPending}
      >
        <p>
          Delete all cube data matching the following context. This operation is
          irreversible.
        </p>
        <ul>
          {activeSelections.map(([key, values]) => (
            <li key={key}>
              <strong>{getFieldLabel(key)}:</strong> {values.join(", ")}
            </li>
          ))}
        </ul>
      </ConfirmOperationDialog>
    </>
  );
}
